import { useCallback, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets'
import { useCreateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useDeleteScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useDeleteScheduledPostTarget'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ScheduledPostTarget } from 'types'
import { ChannelListItem, ChannelItemState } from './ChannelListItem'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { hasContentWarning } from './channelWarnings'
import { isStoryChannel } from '../utils'
import Styles from './ChannelList.module.scss'

interface ChannelListProps {
  crossPostGroup: readonly ExternalIntegrationEnum[]
  almanacFile: AlmanacFileShowItem
  activeProvider?: ExternalIntegrationEnum
  onSelectProvider: (provider: ExternalIntegrationEnum) => void
}

const STORY_LIFETIME_MS = 24 * 60 * 60 * 1000

const isExpiredStory = (channel: DesignChannel | ChannelType | undefined, postedAt: string | undefined): boolean => {
  if (!isStoryChannel(channel)) return false
  if (!postedAt) return false
  const postedMs = Date.parse(postedAt)
  if (Number.isNaN(postedMs)) return false
  return Date.now() - postedMs >= STORY_LIFETIME_MS
}

const expiredStoryTooltip = (channel: DesignChannel | ChannelType | undefined): string => {
  const network = channel === DesignChannel.FacebookStory ? 'Facebook' : 'Instagram'
  return `Since 24 hours have passed, your Story is no longer live. You can still find it in your ${network} archive.`
}

const getChannelState = (
  isConnected: boolean,
  target: ScheduledPostTarget | undefined,
  isPosted: boolean,
  channel: DesignChannel | ChannelType | undefined,
): ChannelItemState => {
  if (isPosted && target?.status === 'published') {
    return isExpiredStory(channel, target.published_at) ? 'posted-expired' : 'posted'
  }
  if (target?.status === 'failed') return 'failed'
  if (target?.status === 'queued' || target?.status === 'processing' || target?.status === 'retrying')
    return 'processing'
  if (!isConnected) return 'unconnected'
  if (isPosted && target) return 'post-now'
  if (target && hasContentWarning(target)) return 'warning'
  return target ? 'connected' : 'connected-disabled'
}

export const ChannelList = ({ crossPostGroup, almanacFile, activeProvider, onSelectProvider }: ChannelListProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { data: integrationsData } = useExternalIntegrations()
  const { data: targets = [] } = useScheduledPostTargets(almanacFile.scheduled_post?.id ?? 0, {
    enabled: !!almanacFile.scheduled_post?.id,
  })

  const scheduledPostId = almanacFile.scheduled_post?.id
  const isPosted = almanacFile.post_status === PostStatusPropertyValue.Posted

  const createMutation = useCreateScheduledPostTarget()
  const deleteMutation = useDeleteScheduledPostTarget()
  const { mutate: publishTargets } = usePublishScheduledPostTargets()
  const { initiateOauth } = useInitiateOauth()
  const { connect: connectAccountPopup } = useConnectAccountPopup()

  const handleToggle = useCallback(
    (provider: ExternalIntegrationEnum, isChecked: boolean) => {
      if (!scheduledPostId) return

      if (isChecked) {
        createMutation.mutate({
          scheduled_post_id: scheduledPostId,
          provider,
          target_overrides: {},
        })
      } else {
        const target = targets.find((t) => t.provider === provider)
        if (!target) return

        deleteMutation.mutate({
          scheduled_post_id: scheduledPostId,
          target_id: target.id,
        })
      }
    },
    [scheduledPostId, targets, createMutation, deleteMutation],
  )

  const handleConnect = useCallback(
    async (provider: ExternalIntegrationEnum) => {
      const popup = await initiateOauth(provider, undefined, '_blank', 'popup=true,width=600,height=780')
      connectAccountPopup(popup as Window | undefined, {
        onConnect: () => {
          queryClient.invalidateQueries(calendarKeys.all)
          queryClient.invalidateQueries(['externalIntegrations', workspaceId])
        },
      })
    },
    [initiateOauth, connectAccountPopup, queryClient, workspaceId],
  )

  const handleRetry = useCallback(
    (provider: ExternalIntegrationEnum) => {
      if (!scheduledPostId) return

      const target = targets.find((t) => t.provider === provider)
      if (!target) return

      publishTargets({
        scheduledPostId,
        targetIds: [target.id],
      })
    },
    [scheduledPostId, targets, publishTargets],
  )

  const channel = almanacFile.channel ?? undefined

  const items = useMemo(() => {
    return crossPostGroup.map((provider) => {
      const integration = integrationsData?.external_integrations?.find((i) => i.provider === provider)
      const target = targets?.find((t) => t.provider === provider)
      const isConnected = !!integration
      const state = getChannelState(isConnected, target, isPosted, channel)

      return {
        provider,
        accountName: integration?.name,
        state,
        postUrl: target?.post_url,
        expiredTooltip: state === 'posted-expired' ? expiredStoryTooltip(channel) : undefined,
      }
    })
  }, [crossPostGroup, integrationsData, targets, isPosted, channel])

  return (
    <div className={Styles.channelList}>
      {items.map((item) => (
        <ChannelListItem
          key={item.provider}
          provider={item.provider}
          accountName={item.accountName}
          state={item.state}
          isSelected={activeProvider === item.provider}
          postUrl={item.postUrl}
          expiredTooltip={item.expiredTooltip}
          onSelect={() => onSelectProvider(item.provider)}
          onToggle={(isChecked) => handleToggle(item.provider, isChecked)}
          onConnect={() => handleConnect(item.provider)}
          onRetry={() => handleRetry(item.provider)}
          onPostNow={() => handleRetry(item.provider)}
        />
      ))}
    </div>
  )
}
